<i18n>
{
  "en": {
    "model": "Model",
    "user": "User",
    "noChanged": "No changes",
    "productBoost": "Product boost",
    "deletedProductBoost": "Deleted boost",
    "saleRateIndexing": "TRate indexing",
    "demandIndexing": "Demand indexing",
    "productIndexing": "Product indexing"
  },
  "es": {
    "model": "Modelo",
    "user": "Usuario",
    "noChanged": "Sin cambios",
    "productBoost": "Product boost",
    "deletedProductBoost": "Boost eliminado",
    "saleRateIndexing": "Indexación de tarifa",
    "demandIndexing": "Indexación de demanda",
    "productIndexing": "Indexación de producto"
  }
}
</i18n>

<template>
  <div class="chip-margins">
    <v-chip v-for="chipConfig in chipsConfig"
            :key="chipConfig.groupCodeName"
            :width="defaultWidth"
            :color="chipConfig.color"
            :disabled="chipConfig.disabled"
            :style="getStyle(chipConfig.borderColor)"
            class="caption d-flex justify-center align-center relative-position"
            :small="useSmallChips"
            :clickable="false">
      {{ chipConfig.label }}
      <v-tooltip v-if="shouldShowIcon(chipConfig.modificationAuthor)" top>
        <template v-slot:activator="{ on, attrs }">
          <div v-on="on" v-bind="attrs">
            <v-icon color="primary"
                    class="info-icon">
              mdi-information
            </v-icon>
          </div>
        </template>
        <span>{{ chipConfig.modificationAuthor }}</span>
      </v-tooltip>
    </v-chip>
  </div>
</template>

<script>
import Component from 'vue-class-component';
import ChipGroup from '@optimizer/components/ChipGroup.vue';
import { Prop } from 'vue-property-decorator';
import { MapGetters } from '@core/helpers/decorators';
import { ProductRecommendation } from '@priceOptimizer/models';

@Component
@MapGetters(['groupList'])
export default class IndexedProductPriceChips extends ChipGroup {
  @Prop({ type: Boolean, default: false }) disabled;

  @Prop({
    validator(value) {
      return value instanceof ProductRecommendation || value === false;
    }
  }) productRecommendation;

  get chipsConfig() {
    const groupInfoParsed = this.parseGroupInfo(this.groupInfo);
    return this.groupList.map((group) => {
      const { color, code_name: groupCodeName } = group;
      const groupLabel = groupInfoParsed[groupCodeName]?.label;
      const label = groupLabel || this.productRecommendation.getPriceRangeLabel(group.code_name, this.productRecommendation.confirmed).replace(/[▼▲=]/, '');
      const { disabled } = this;
      const clickDisabled = groupInfoParsed[groupCodeName]?.clickDisabled || !groupLabel || false;
      const borderColor = groupInfoParsed[groupCodeName]?.borderColor || false;
      const modificationAuthor = groupInfoParsed[groupCodeName]?.modificationAuthor || '';

      return {
        groupCodeName, label, color, disabled, clickDisabled, borderColor, modificationAuthor, haveCodeName: groupLabel
      };
    });
  }

  get useSmallChips() {
    return this.$vuetify.breakpoint.smAndDown;
  }

  parseGroupInfo(indexedProductPrice) {
    const parser = {};
    indexedProductPrice.indexedPriceByDemand.forEach((element) => {
      parser[element.demandType] = {
        label: element.price.toString(),
        disabled: this.disabled,
        clickDisabled: true,
        modificationAuthor: this.$t(this.getModifiedLabel(element.modificationAuthor))
      };
    });
    return parser;
  }

  shouldShowIcon(author) {
    const excludedAuthors = ['Modelo', 'Model', 'Sin cambios', 'No changed', ''];
    return !excludedAuthors.includes(author);
  }

  getModifiedLabel(type) {
    switch (type) {
      case 'Modelo': return 'model';
      case 'Model': return 'model';
      case 'Usuario': return 'user';
      case 'User': return 'user';
      case 'Sin cambios': return 'noChanged';
      case 'No changed': return 'noChanged';
      case 'Product Boost': return 'productBoost';
      case 'Product Boost borrado': return 'deletedProductBoost';
      case 'Deleted Product Boost': return 'deletedProductBoost';
      case 'Indexación de tarifa': return 'saleRateIndexing';
      case 'Sale rate indexing': return 'saleRateIndexing';
      case 'Indexación de demanda': return 'demandIndexing';
      case 'Demand indexing': return 'demandIndexing';
      case 'Indexación de producto': return 'productIndexing';
      case 'Product indexing': return 'productIndexing';
    }
    return '';
  }
}
</script>

<style scoped lang="scss">
.chip-no-visible {
  visibility: hidden;
}
.chip-margins {
  * {
    margin: 3px 5px;
  }

  :first-child {
    margin: 0 0;
  }

  :last-child {
    margin: 0 0;
  }

  .relative-position {
    position: relative;
  }

  .info-icon {
    position: absolute;
    top: 4px;
    right: 4px;
    // Tamaño por defecto del icono para pantallas grandes
    font-size: 24px;
  }

  // Ajusta el tamaño y la posición del icono para pantallas medianas
  @media (max-width: 960px) {
    .info-icon {
      font-size: 21px;
      top: 3px;
      right: 3px;
    }
  }

  // Ajusta el tamaño y la posición del icono para pantallas pequeñas
  @media (max-width: 600px) {
    .info-icon {
      font-size: 21px;
      top: 2px;
      right: 2px;
    }
  }
}
</style>
